import type { Module, ProgressData } from './types';

import { m1 } from './m1-positionality';
import { m2 } from './m2-cognition';
import { m3 } from './m3-social';
import { m4 } from './m4-power';
import { m5 } from './m5-systems';
import { m6 } from './m6-economy';
import { m7 } from './m7-digital';
import { m8 } from './m8-synthesis';

export { type Module, type Lesson, type ProgressData } from './types';

export const CURRICULUM: Module[] = [m1, m2, m3, m4, m5, m6, m7, m8];

export function getModuleById(id: string): Module | undefined {
  return CURRICULUM.find((m) => m.id === id);
}

export function getLessonById(moduleId: string, lessonId: string) {
  const mod = getModuleById(moduleId);
  if (!mod) return { module: undefined, lesson: undefined };
  return {
    module: mod,
    lesson: mod.lessons.find((l) => l.id === lessonId),
  };
}

export function getAdjacentLesson(moduleId: string, lessonId: string) {
  const allLessons = CURRICULUM.flatMap((m) =>
    m.lessons.map((l) => ({ ...l, moduleId: m.id, moduleTitle: m.title }))
  );
  const idx = allLessons.findIndex(
    (l) => l.moduleId === moduleId && l.id === lessonId
  );
  return {
    prev: idx > 0 ? allLessons[idx - 1] : undefined,
    next: idx < allLessons.length - 1 ? allLessons[idx + 1] : undefined,
    current: idx >= 0 ? allLessons[idx] : undefined,
    total: allLessons.length,
  };
}

export function getTotalLessons(): number {
  return CURRICULUM.reduce((sum, m) => sum + m.lessons.length, 0);
}

export function getModuleProgress(
  moduleId: string,
  progress: ProgressData
): { completed: number; total: number; percentage: number } {
  const mod = getModuleById(moduleId);
  if (!mod) return { completed: 0, total: 0, percentage: 0 };
  const total = mod.lessons.length;
  const completed = mod.lessons.filter(
    (l) => progress.completedLessons[l.id]
  ).length;
  return { completed, total, percentage: Math.round((completed / total) * 100) };
}

export function getOverallProgress(progress: ProgressData): {
  completed: number;
  total: number;
  percentage: number;
} {
  const total = getTotalLessons();
  const completed = Object.values(progress.completedLessons).filter(Boolean).length;
  return { completed, total, percentage: Math.round((completed / total) * 100) };
}

export const DEFAULT_PROGRESS: ProgressData = {
  completedLessons: {},
  lessonNotes: {},
  currentModule: null,
  currentLesson: null,
  startedAt: new Date().toISOString(),
  lastActivityAt: new Date().toISOString(),
};

export const MODULE_META = [
  { number: 1, icon: 'Eye', label: 'Positionality & Epistemics' },
  { number: 2, icon: 'Brain', label: 'Cognition & Experience' },
  { number: 3, icon: 'Users', label: 'Social Construction' },
  { number: 4, icon: 'Shield', label: 'Power & Coloniality' },
  { number: 5, icon: 'Network', label: 'Systems & Ecology' },
  { number: 6, icon: 'Landmark', label: 'Political Economy' },
  { number: 7, icon: 'Cpu', label: 'Digital Paradigm & AI' },
  { number: 8, icon: 'Compass', label: 'Navigation & Praxis' },
];
